import { api } from '@goaltree/backend/convex/_generated/api'
import type { Focus } from '@goaltree/backend/convex/lib/focus'
import { useRouterState } from '@tanstack/react-router'
import { useConvexAuth, useQuery } from 'convex/react'
import { useState } from 'react'

import { useSignedIn } from '@/hooks/use-signed-in'
import { hasSessionIndicator } from '@/lib/session-indicator'
import { useOnboardingStore } from '@/store/onboarding.store'

// Step 1 is "What brings you here?" (on /goals/new). The user-profile screen
// covers habits and coach; it adds its own sub-step.
const STEPS: Record<string, number> = {
  '/goals/new': 2,
  '/goals/new/questions': 3,
  '/goals/new/preview': 4,
  '/onboarding/user-profile': 5,
}

export const ONBOARDING_STEP_COUNT = 6
// Habits only: focus → habits → coach.
const HABITS_STEP_COUNT = 3

export function useOnboardingMode() {
  const pathname = useRouterState({ select: (s) => s.location.pathname.replace(/\/$/, '') })
  const { isAuthenticated, isLoading } = useConvexAuth()
  const signedIn = useSignedIn()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const goals = useQuery(api.nodes.getUserGoals, isAuthenticated ? {} : 'skip')
  const pendingPlan = useOnboardingStore((s) => s.autoSaveAfterAuth && !!s.generatedTree)
  const pickedFocus = useOnboardingStore((s) => s.focus)
  const focus = pickedFocus ?? (prefs as { focus?: Focus } | undefined)?.focus
  const onProfile = pathname === '/onboarding/user-profile'
  const habitsOnly = focus === 'habits' && onProfile
  const step = habitsOnly ? 2 : (STEPS[pathname] ?? 0)

  const anonymous = signedIn === 'out' && !(isLoading && hasSessionIndicator())
  const firstRun =
    !!prefs &&
    !(prefs as { onboardingCompletedAt?: number }).onboardingCompletedAt &&
    goals !== undefined &&
    !goals.some((g) => !g.isInbox)
  const live = step > 0 && (onProfile || anonymous || firstRun || pendingPlan)

  // Saving the first plan creates a goal (so firstRun flips false) a render
  // before the navigation to habits; stay in onboarding mode through it.
  const [latched, setLatched] = useState(false)
  if (live && !latched) setLatched(true)

  return {
    active: step > 0 && (live || latched),
    step,
    total: habitsOnly ? HABITS_STEP_COUNT : ONBOARDING_STEP_COUNT,
    focus,
  }
}
